import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
} from '@angular/animations';
import { Subscription } from 'rxjs';
import { ContactInfo } from 'src/app/@shared/components/contact-view/contact.view.component';
import {
  MINISTRY_CONTACT_DEFAULT_INFO,
  MinistryContactInfoService,
} from 'src/app/@shared/services/ministry-contact-info/ministry-contact-info.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { Overlay } from '@angular/cdk/overlay';
import { OverlayComponent } from 'src/app/@shared/components/design/overlay/overlay.component';
import { RoutePlanningService } from '../../../services/route-planning/route-planning.service';
import { RoutePlannerFormComponent } from '../../../components/route-planner-form/route-planner-form.component';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  CAR_INTEGRATED_ROUTE,
  INTERCITY_LINES,
  OPEN_SIDE_MENU,
  ORIGIN_DESTINATION,
  SEARCH_BY_LINE,
  SEARCH_BY_STATION,
} from '../../../constants/multi-language.constants';
import { Direction } from '@angular/cdk/bidi';
import { eTab } from '../../../enums/route-planning/tabs/tabs.enums';

@Component({
  selector: 'app-route-planning-forms-container',
  templateUrl: './route-planning-forms-container.component.html',
  styleUrls: ['./route-planning-forms-container.component.scss'],
  standalone: false,
  animations: [
    trigger('tabSlide', [
      transition(':increment', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('150ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':decrement', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('150ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class RoutePlannerFormsContainerComponent
  extends OverlayComponent
  implements AfterViewInit, OnDestroy {
  @ViewChildren('form') forms: QueryList<RoutePlannerFormComponent>;
  @ViewChild('container') routePlannerContainer: ElementRef<HTMLDivElement>;
  @ViewChild('tabLabelsContainer') tabLabelsContainer: ElementRef<HTMLDivElement>;
  @ViewChildren('tabLabelBtn') tabLabelBtns: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('sourceDestinationForm') sourceDestinationForm: any;
  @ViewChild('searchLineForm') searchLineForm: any;
  @ViewChild('searchStationForm') searchStationForm: any;
  @ViewChild('linesBetweenCitiesForm') linesBetweenCitiesForm: any;
  @ViewChild('carIntegratedForm') carIntegratedForm: any;

  activeForm: RoutePlannerFormComponent = null;
  selectedTabIndex: number = 0;
  /** Tracks which tab button owns tabindex="0" (roving focus). Range: 0..N-1 */
  focusedTablistIndex: number = 0;
  tabLabels: string[] = [];
  readonly tabFormIds: eTab[] = [
    eTab.SOURCE_DESTINATION,
    eTab.LINES,
    eTab.STATIONS,
    eTab.INTER_CITY_LINE,
    eTab.CAR_INTERGRATED,
  ];

  isSearchButtonDisabled: boolean = false;
  isShowLoader: boolean = false;

  originDestinationText: string;
  searchLineText: string;
  searchStationText: string;
  interCityText: string;
  routeWithCarText: string;
  openSideMenuAriaLabel: string = OPEN_SIDE_MENU.hebrew;

  direction: Direction;

  openMenu: boolean = false;

  title: string = '';

  /** Mobile drawer: ministry contact modal (same data as desktop nav-bar). */
  isContactOpen = false;
  contactInfo: ContactInfo = { ...MINISTRY_CONTACT_DEFAULT_INFO };
  private ministryContactSub?: Subscription;

  constructor(
    protected override readonly resizeService: ResizeService,
    protected override readonly overlay: Overlay,
    protected readonly queryParamsService: QueryParamsService,
    protected readonly routePlanningService: RoutePlanningService,

    private readonly cdr: ChangeDetectorRef,
    private readonly languageService: LanguageService,
    private readonly ministryContactInfo: MinistryContactInfoService
  ) {
    super(overlay, resizeService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // this.queryParamsService.ngOnInit();
    this.routePlanningService.ngOnInit();
    this.initializeMultiLanguageTexts();
    this.ministryContactSub = this.ministryContactInfo.contactInfo$.subscribe(
      (info) => (this.contactInfo = info)
    );
  }

  override ngOnDestroy(): void {
    this.ministryContactSub?.unsubscribe();
    if (this.isContactOpen && typeof document !== 'undefined') {
      document.body.classList.remove('modal-open');
    }
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    this.initializeTabGroup();

    this.forms.changes.subscribe(() => {
      if (this.forms.first) {
        this.activeForm = this.forms.first;
      }
    });

    this.routePlanningService._componentOverlaySub.subscribe((newPortal) => {
      // Handle close overlay case
      if (newPortal === null || newPortal?.componentType === null) {
        this.closeAllOverlays();
        return;
      }

      const componentType = newPortal?.componentType;
      const data = newPortal?.data;

      if (!!componentType) {
        this.openComponentOverlay(
          this.direction,
          componentType,
          data,
          this.routePlannerContainer,
          (value) => {
            if (!!data?.closeCallbackFn) data.closeCallbackFn(value);
          }
        );
      }
    });

    this.cdr.detectChanges();
  }

  private closeAllOverlays(): void {
    Object.keys(this.overlayEntries).forEach((refId) =>
      this.detachOverlayImmediate(refId, undefined, false)
    );
  }

  setTitle(): void {
    switch (this.selectedTabIndex) {
      case 0:
        this.title = this.originDestinationText;
        break;
      case 1:
        this.title = this.searchLineText;
        break;
      case 2:
        this.title = this.searchStationText;
        break;
      case 3:
        this.title = this.interCityText;
        break;
      default:
        this.title = this.originDestinationText;
        break;
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.originDestinationText = ORIGIN_DESTINATION[language.language];
      this.searchLineText = SEARCH_BY_LINE[language.language];
      this.searchStationText = SEARCH_BY_STATION[language.language];
      this.interCityText = INTERCITY_LINES[language.language];
      this.routeWithCarText = CAR_INTEGRATED_ROUTE[language.language];
      this.openSideMenuAriaLabel = OPEN_SIDE_MENU[language.language];
      this.tabLabels = [
        this.originDestinationText,
        this.searchLineText,
        this.searchStationText,
        this.interCityText,
        this.routeWithCarText,
      ];
      this.setTitle();
    });
  }

  private initializeTabGroup() {
    this.activeForm = this.forms.first;

    // Check if URL parameters are present and valid
    const queryParams = this.queryParamsService.currentParams;
    const isSharedUrl = this.queryParamsService.isNavigatingToSharedUrl;
    const hasValidParams = this.validateQueryParams(queryParams);
    const isInitialParamsProcessed =
      this.queryParamsService.initialParamsProcessed;

    console.debug('Initializing tab group:', {
      isSharedUrl,
      hasValidParams,
      initialParamsProcessed: isInitialParamsProcessed,
      queryParams,
    });

    // Special handling for inter-city tab
    if (queryParams && queryParams['tab'] === 'inter-city') {
      console.debug('Detected inter-city tab in URL parameters');

      const interCityTabIndex = this.tabFormIds.indexOf(eTab.INTER_CITY_LINE);

      if (interCityTabIndex !== -1) {
        console.debug('Setting active tab to inter-city tab');
        this.selectedTabIndex = interCityTabIndex;
        this.setTitle();
        this.cdr.detectChanges();
        this.activeForm = this.forms.first;
      }
    }

    // Only process shared URL parameters if they haven't been processed yet
    if (isSharedUrl && !isInitialParamsProcessed) {
      console.debug('Processing shared URL parameters');

      // Autofill the form with the URL parameters
      setTimeout(() => {
        console.debug('Autofilling form with URL parameters');
        this.activeForm.autofillQueryParams();
        this.queryParamsService.markInitialParamsAsProcessed();
      }, 100);
    } else if (isSharedUrl && isInitialParamsProcessed) {
      console.debug(
        'Shared URL parameters already processed, skipping autofill'
      );
    } else if (!hasValidParams && (queryParams['org'] || queryParams['dest'])) {
      // If parameters are invalid, clear them to prevent showing no results immediately
      console.warn('Invalid URL parameters detected, clearing parameters');
      this.queryParamsService.clearQueryParams(['tab']);
    }

    const currentFormId = this.tabFormIds[this.selectedTabIndex];
    if (!this.queryParamsService.currentParams['tab']) {
      this.queryParamsService.setQueryParams({
        tab: currentFormId,
      });
    } else {
      this.queryParamsService.setQueryParams({
        saveParams: true,
      });
    }

    // Track if we've already processed a triggerForm parameter
    let triggerFormProcessed = false;

    this.queryParamsService.queryParams$.subscribe((queryParams) => {
      const currentFormIdInner = this.tabFormIds[this.selectedTabIndex];
      if (
        !!queryParams['tab'] &&
        currentFormIdInner != queryParams['tab']
      ) {
        console.debug(`Tab changed in URL to: ${queryParams['tab']}`);
        setTimeout(() => {
          const tabIndex = this.tabFormIds.indexOf(queryParams['tab'] as eTab);

          if (tabIndex !== -1) {
            console.debug(`Setting selected tab index to: ${tabIndex}`);
            this.selectedTabIndex = tabIndex;
            this.setTitle();
            this.cdr.detectChanges();
            this.activeForm = this.forms.first;
            this.activeForm.autofillQueryParams();
          }
        });
      }

      // Only process triggerForm once to prevent duplicate submissions
      if (queryParams['triggerForm'] && !triggerFormProcessed) {
        console.debug('Triggering form autofill due to triggerForm parameter');
        triggerFormProcessed = true;
        setTimeout(() => {
          this.activeForm.autofillQueryParams();
          this.queryParamsService.setQueryParam('triggerForm', null);
        }, 100);
      }
    });
  }

  // Helper method to validate query parameters
  private validateQueryParams(queryParams: any): boolean {
    // Check if origin parameters are valid
    if (queryParams['org'] && queryParams['oll']) {
      const originCoords = queryParams['oll'].split('_');
      if (
        originCoords.length !== 2 ||
        isNaN(parseFloat(originCoords[0])) ||
        isNaN(parseFloat(originCoords[1]))
      ) {
        return false;
      }
    }

    // Check if destination parameters are valid
    if (queryParams['dest'] && queryParams['dll']) {
      const destCoords = queryParams['dll'].split('_');
      if (
        destCoords.length !== 2 ||
        isNaN(parseFloat(destCoords[0])) ||
        isNaN(parseFloat(destCoords[1]))
      ) {
        return false;
      }
    }

    // Check if stop parameters are valid
    if (queryParams['stop'] && queryParams['sll']) {
      const stopCoords = queryParams['sll'].split('_');
      if (
        stopCoords.length !== 2 ||
        isNaN(parseFloat(stopCoords[0])) ||
        isNaN(parseFloat(stopCoords[1]))
      ) {
        return false;
      }
    }

    // If we have origin but no destination or vice versa, it's invalid
    if (
      (queryParams['org'] && !queryParams['dest']) ||
      (!queryParams['org'] && queryParams['dest'])
    ) {
      return false;
    }

    return true;
  }

  selectTab(index: number): void {
    if (index === this.selectedTabIndex || index < 0 || index >= this.tabLabels.length) return;
    this.focusedTablistIndex = index;
    this.onSelectedTabChange(index);
  }

  onPrevTab(): void {
    if (this.selectedTabIndex > 0) {
      this.selectTab(this.selectedTabIndex - 1);
    }
  }

  onNextTab(): void {
    if (this.selectedTabIndex < this.tabLabels.length - 1) {
      this.selectTab(this.selectedTabIndex + 1);
    }
  }

  /**
   * WAI-ARIA Tabs keyboard interaction (roving tabindex on tab buttons only).
   * Arrow keys cycle focus among tabs; Enter/Space activates the focused tab
   * without moving focus away. The arrow buttons are separate tab-stops outside
   * this group.
   */
  onTablistKeydown(event: KeyboardEvent): void {
    const isRtl = this.direction === 'rtl';
    const nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const prevKey = isRtl ? 'ArrowRight' : 'ArrowLeft';

    const lastIndex = this.tabLabels.length - 1;

    switch (event.key) {
      case nextKey: {
        event.preventDefault();
        const next = this.focusedTablistIndex >= lastIndex ? 0 : this.focusedTablistIndex + 1;
        this.focusTablistSlot(next);
        break;
      }
      case prevKey: {
        event.preventDefault();
        const prev = this.focusedTablistIndex <= 0 ? lastIndex : this.focusedTablistIndex - 1;
        this.focusTablistSlot(prev);
        break;
      }
      case 'Home':
        event.preventDefault();
        this.focusTablistSlot(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusTablistSlot(lastIndex);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectTab(this.focusedTablistIndex);
        break;
    }
  }

  private focusTablistSlot(slot: number): void {
    this.focusedTablistIndex = slot;
    const btns = this.tabLabelBtns?.toArray();
    btns?.[slot]?.nativeElement?.focus();
  }

  onSelectedTabChange(newIndex: number) {
    const previousTab = this.tabFormIds[this.selectedTabIndex];

    this.selectedTabIndex = newIndex;
    this.setTitle();

    const newTab = this.tabFormIds[this.selectedTabIndex];

    if (previousTab !== newTab) {
      switch (newTab) {
        case eTab.LINES:
          this.searchLineForm?.onTabLeave?.();
          this.searchLineForm?.autofillQueryParams();
          break
        case eTab.STATIONS:
          this.searchStationForm?.onTabLeave?.();
          this.searchStationForm?.autofillQueryParams();
          break
        case eTab.INTER_CITY_LINE:
          this.linesBetweenCitiesForm?.onTabLeave?.();
          this.linesBetweenCitiesForm?.autofillQueryParams();
          break
      }

      switch (previousTab) {
        case eTab.SOURCE_DESTINATION:
          this.sourceDestinationForm?.onTabLeave?.();
          break
        case eTab.LINES:
          this.searchLineForm?.onTabLeave?.();
          break
        case eTab.STATIONS:
          this.searchStationForm?.onTabLeave?.();
          break
        case eTab.INTER_CITY_LINE:
          this.linesBetweenCitiesForm?.onTabLeave?.();
          break
        case eTab.CAR_INTERGRATED:
          this.carIntegratedForm?.onTabLeave?.();
          break
      }

      this.queryParamsService.clearQueryParams(['tab']);
      this.cdr.detectChanges();
      this.scrollActiveTabIntoView();
      this.activeForm = this.forms.first;

      if (previousTab !== newTab) {
        switch (newTab) {
          case eTab.LINES:
            this.searchLineForm?.onTabLeave?.();
            break
          case eTab.STATIONS:
            this.searchStationForm?.onTabLeave?.();
            break
          case eTab.INTER_CITY_LINE:
            this.linesBetweenCitiesForm?.onTabLeave?.();
            break
        }
      }
    }


    // Check if we're switching away from the source-destination tab
    // const isLeavingSourceDestinationTab =
    //   previousTab === 'source-destination' &&
    //   newTab !== 'source-destination' &&
    //   queryParams['routeId'];

    // // Check if we're switching away from the lines tab
    // const isLeavingLinesTab =
    //   previousTab === 'lines' &&
    //   newTab !== 'lines' &&
    //   (queryParams['line'] ||
    //     (queryParams['routeId'] && queryParams['oppositeRouteId']));

    // // Check if we're switching away from the stations tab
    // const isLeavingStationsTab =
    //   previousTab === 'stations' && newTab !== 'stations' && queryParams['poi'];

    // // Check if we're switching away from the inter-city tab
    // const isLeavingInterCityTab =
    //   previousTab === 'inter-city' &&
    //   newTab !== 'inter-city' &&
    //   (queryParams['originCity'] ||
    //     queryParams['destinationCity'] ||
    //     queryParams['org'] ||
    //     queryParams['dest']);

    // // Check if we're switching to the inter-city tab
    // const isSwitchingToInterCityTab = newTab === 'inter-city';

    // // If we're processing a shared URL, don't clear parameters
    // if (this.queryParamsService.isNavigatingToSharedUrl) {
    //   console.debug('Preserving parameters during shared URL navigation');
    // } else if (!queryParams['saveParams']) {
    //   // Clear all query params except 'tab'
    //   this.queryParamsService.clearQueryParams(['tab']);
    // } else if (isLeavingSourceDestinationTab) {
    //   // If we're leaving the source-destination tab and have a routeId,
    //   // clear it to prevent auto-navigation on refresh
    //   console.debug('Clearing routeId when leaving source-destination tab');
    //   this.queryParamsService.setQueryParam('routeId', null);
    // } else if (isLeavingLinesTab) {
    //   // If we're leaving the lines tab, clear line-specific parameters
    //   console.debug('Clearing line parameters when leaving lines tab');
    //   this.queryParamsService.setQueryParams({
    //     line: null,
    //     routeId: null,
    //     oppositeRouteId: null,
    //     boardingStation: null,
    //     dropingStation: null,
    //   });
    // } else if (isLeavingStationsTab) {
    //   // If we're leaving the stations tab, clear station-specific parameters
    //   console.debug('Clearing station parameters when leaving stations tab');
    //   this.queryParamsService.setQueryParams({
    //     poi: null,
    //     poiLat: null,
    //     poiLng: null,
    //   });
    // } else if (isLeavingInterCityTab) {
    //   // If we're leaving the inter-city tab, clear inter-city-specific parameters
    //   console.debug(
    //     'Clearing inter-city parameters when leaving inter-city tab'
    //   );
    //   this.queryParamsService.setQueryParams({
    //     originCity: null,
    //     destinationCity: null,
    //     triggerForm: null,
    //   });
    // }

    // // If we're switching to the inter-city tab, handle parameter conversion
    // if (isSwitchingToInterCityTab) {
    //   // If we have org/dest parameters, convert them to originCity/destinationCity
    //   if (queryParams['org'] && queryParams['dest']) {
    //     console.debug(
    //       'Converting org/dest to originCity/destinationCity for inter-city tab'
    //     );
    //     this.queryParamsService.setQueryParams({
    //       org: queryParams['org'],
    //       dest: queryParams['dest'],
    //     });
    //   }

    //   // Clear source-destination parameters to avoid conflicts
    //   const paramsToRemove = ['org', 'oll', 'dest', 'dll', 'routeId'];
    //   paramsToRemove.forEach((param) => {
    //     if (queryParams[param]) {
    //       console.debug(
    //         `Clearing conflicting parameter ${param} for inter-city tab`
    //       );
    //       this.queryParamsService.setQueryParam(param, null);
    //     }
    //   });
    // }

    this.queryParamsService.setQueryParams({
      tab: newTab,
      saveParams: true,
    });
    this.queryParamsService._tabChanged$.next(newTab);
  }

  setOpenMenu(open: boolean) {
    this.openMenu = open;
  }

  /** Burger side menu → same action as desktop “Contact” (CMS-backed modal). */
  onSideMenuOpenContact(): void {
    this.setOpenMenu(false);
    this.isContactOpen = true;
    if (typeof document !== 'undefined') {
      document.body.classList.add('modal-open');
    }
  }

  onMinistryContactClose(): void {
    this.isContactOpen = false;
    if (typeof document !== 'undefined') {
      document.body.classList.remove('modal-open');
    }
  }

  private scrollActiveTabIntoView(): void {
    const container = this.tabLabelsContainer?.nativeElement;
    const btns = this.tabLabelBtns?.toArray();
    if (!container || !btns || !btns[this.selectedTabIndex]) return;

    const btn = btns[this.selectedTabIndex].nativeElement;
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    if (btnRect.left < containerRect.left) {
      container.scrollLeft += btnRect.left - containerRect.left;
    } else if (btnRect.right > containerRect.right) {
      container.scrollLeft += btnRect.right - containerRect.right;
    }
  }

}
